import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaShoppingBag, FaSearch, FaLock, FaCheckCircle } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { ContentThumb, TeacherAvatar, TagBadges, typeMeta } from "../../components/marketplace";

// YouTube-style student marketplace home: a flat feed of content items across
// all teachers, filterable by type + search, plus a "Subscriptions" view. Cards
// open the watch page (/marketplace/watch/:id); teacher rows open the channel
// (/marketplace/teacher/:id).

const CHIPS = [
    ["all", "All"],
    ["video", "Videos"],
    ["lesson_notes", "Lesson Notes"],
    ["revision_guide", "Revision Guides"],
    ["practice_test", "Practice Tests"],
    ["exam_prep", "Exam Prep"],
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

const Marketplace = () => {
    const navigate = useNavigate();
    const [view, setView] = useState("home"); // home | subs
    const [feed, setFeed] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const [fRes, sRes] = await Promise.all([
                authFetch("/api/marketplace/feed"),
                authFetch("/api/marketplace/my-subscriptions"),
            ]);
            const fData = await fRes.json().catch(() => ({}));
            const sData = await sRes.json().catch(() => ({}));
            setFeed(fData.data || []);
            setSubscriptions(sData.data || []);
        } catch (err) {
            toast.error("Could not load the marketplace");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCancel = async (subId) => {
        setBusyId(subId);
        try {
            const res = await authFetch(`/api/marketplace/subscriptions/${subId}/cancel`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to cancel");
            toast.success(data.message || "Auto-renew turned off");
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return feed.filter((it) => {
            if (typeFilter !== "all" && it.type !== typeFilter) return false;
            if (!needle) return true;
            return (
                (it.title || "").toLowerCase().includes(needle) ||
                (it.teacher?.name || "").toLowerCase().includes(needle) ||
                (it.subjects || []).some((s) => s.toLowerCase().includes(needle)) ||
                (it.classes || []).some((c) => c.toLowerCase().includes(needle))
            );
        });
    }, [feed, q, typeFilter]);

    return (
        <div className="min-h-screen bg-gray-50 font-montserrat">
            {/* Top bar */}
            <nav className="bg-[#193FAE] shadow-md px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
                <button onClick={() => navigate("/dashboard")} className="text-white shrink-0" aria-label="Back">
                    <FaArrowLeft size={18} />
                </button>
                <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 shrink-0">
                    <FaShoppingBag /> <span className="hidden sm:inline">Marketplace</span>
                </h1>

                {/* Search */}
                <div className="flex-1 max-w-xl mx-auto relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search content or teachers"
                        className="w-full bg-white/95 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
                    />
                </div>

                <div className="flex gap-1 bg-white/10 rounded-full p-1 shrink-0">
                    {[["home", "Home"], ["subs", "Subscriptions"]].map(([v, label]) => (
                        <button
                            key={v}
                            onClick={() => setView(v)}
                            className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                                view === v ? "bg-white text-blue600" : "text-white/90 hover:bg-white/10"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* Category chips (home only) */}
            {view === "home" && (
                <div className="bg-white border-b border-gray-100 sticky top-[56px] z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex gap-2 overflow-x-auto no-scrollbar">
                        {CHIPS.map(([val, label]) => (
                            <button
                                key={val}
                                onClick={() => setTypeFilter(val)}
                                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                                    typeFilter === val
                                        ? "bg-blue600 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                {loading && <p className="text-gray-500">Loading…</p>}

                {/* ---- Feed ---- */}
                {view === "home" && !loading && (
                    <>
                        {filtered.length === 0 ? (
                            <EmptyState
                                title={feed.length === 0 ? "No content yet" : "Nothing matches your search"}
                                subtitle={
                                    feed.length === 0
                                        ? "Teachers haven't published any content to the marketplace yet. Check back soon."
                                        : "Try a different keyword or category."
                                }
                            />
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6">
                                {filtered.map((item) => (
                                    <FeedCard key={item.id} item={item} onOpen={() => navigate(`/marketplace/watch/${item.id}`)}
                                        onTeacher={() => navigate(`/marketplace/teacher/${item.teacher?.id}`)} />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* ---- My subscriptions ---- */}
                {view === "subs" && !loading && (
                    <div className="space-y-6">
                        {subscriptions.length === 0 && (
                            <EmptyState
                                title="No subscriptions yet"
                                subtitle="Subscribe to a teacher to unlock all their content. Browse the Home feed to get started."
                                action={<button onClick={() => setView("home")} className="mt-4 bg-blue600 text-white px-5 py-2 rounded-lg font-semibold text-sm">Browse content</button>}
                            />
                        )}
                        {subscriptions.map((s) => (
                            <SubscriptionBlock
                                key={s.id}
                                sub={s}
                                busy={busyId === s.id}
                                onCancel={() => handleCancel(s.id)}
                                onChannel={() => navigate(`/marketplace/teacher/${s.teacher_id}`)}
                                onOpen={(cid) => navigate(`/marketplace/watch/${cid}`)}
                                fmtDate={fmtDate}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// A single feed item card (thumbnail + teacher row), YouTube-style.
const FeedCard = ({ item, onOpen, onTeacher }) => {
    const locked = !item.subscribed;
    return (
        <div className="group cursor-pointer">
            <div className="relative" onClick={onOpen}>
                <ContentThumb type={item.type} thumbnailUrl={item.thumbnail_url} title={item.title} />
                {locked ? (
                    <span className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
                        <FaLock size={9} /> Subscribe
                    </span>
                ) : (
                    <span className="absolute top-1.5 left-1.5 bg-green-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
                        <FaCheckCircle size={9} /> Unlocked
                    </span>
                )}
            </div>
            <div className="flex gap-3 mt-3">
                <button onClick={onTeacher} className="shrink-0">
                    <TeacherAvatar name={item.teacher?.name} src={item.teacher?.profile_image} size={36} />
                </button>
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug" onClick={onOpen}>
                        {item.title}
                    </h3>
                    <button onClick={onTeacher} className="block text-xs text-gray-500 hover:text-gray-800 mt-1 truncate max-w-full text-left">
                        {item.teacher?.name}
                    </button>
                    <TagBadges subjects={item.subjects} classes={item.classes} max={3} className="mt-1.5" />
                    <p className="text-xs text-gray-400 mt-1">
                        {typeMeta(item.type).label}
                        {item.price != null ? ` · ₦${Number(item.price).toLocaleString()}/mo` : ""}
                    </p>
                </div>
            </div>
        </div>
    );
};

// One subscribed teacher: channel header + a grid of their unlocked content.
const SubscriptionBlock = ({ sub, busy, onCancel, onChannel, onOpen, fmtDate }) => (
    <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-start justify-between gap-3">
            <button onClick={onChannel} className="flex items-center gap-3 text-left min-w-0">
                <TeacherAvatar name={sub.teacherName} size={44} />
                <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{sub.teacherName}</h3>
                    <p className="text-xs text-gray-400">
                        ₦{sub.amount?.toLocaleString()}/month · {sub.status === "cancelled" ? "Ends" : "Renews"} {fmtDate(sub.renewsOn)}
                    </p>
                </div>
            </button>
            {sub.status === "cancelled" ? (
                <span className="text-xs text-gray-400 shrink-0">Auto-renew off</span>
            ) : (
                <button onClick={onCancel} disabled={busy} className="text-red-500 text-xs font-semibold disabled:opacity-50 shrink-0">
                    {busy ? "…" : "Cancel"}
                </button>
            )}
        </div>

        <div className="mt-4 border-t border-gray-100 pt-4">
            {(sub.content || []).length === 0 ? (
                <p className="text-xs text-gray-400">This teacher hasn't published any content yet.</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-4">
                    {sub.content.map((c) => (
                        <div key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                            <ContentThumb type={c.type} thumbnailUrl={c.thumbnail_url} title={c.title} />
                            <p className="text-xs font-medium text-gray-800 mt-1.5 line-clamp-2 leading-snug">{c.title}</p>
                            <TagBadges subjects={c.subjects} classes={c.classes} max={3} className="mt-1" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
);

const EmptyState = ({ title, subtitle, action }) => (
    <div className="text-center py-20">
        <div className="w-16 h-16 rounded-full bg-blue50 text-blue600 flex items-center justify-center mx-auto">
            <FaShoppingBag size={26} />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-800">{title}</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">{subtitle}</p>
        {action}
    </div>
);

export default Marketplace;
